import React, { useState, useRef, useEffect } from "react";
import { X, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface TopicMultiSelectProps {
  availableTopics: string[];
  selectedTopics: string[];
  onChange: (topics: string[]) => void;
  placeholder?: string;
}

export const TopicMultiSelect: React.FC<TopicMultiSelectProps> = ({
  availableTopics,
  selectedTopics,
  onChange,
  placeholder = "Optional: Select concepts to focus on",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleTopic = (topic: string) => {
    onChange(
      selectedTopics.includes(topic)
        ? selectedTopics.filter((t) => t !== topic)
        : [...selectedTopics, topic]
    );
  };

  const removeTopic = (topic: string) => {
    onChange(selectedTopics.filter((t) => t !== topic));
  };

  const selectAll = () => onChange([...availableTopics]);
  const clearAll = () => onChange([]);

  const allSelected =
    availableTopics.length > 0 && selectedTopics.length === availableTopics.length;

  const filteredTopics = availableTopics.filter((t) =>
    t.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [dropdownOpen]);

  return (
    <div ref={containerRef} className="relative">
      {/* Input area with pills */}
      <div
        className="min-h-10 w-full cursor-text rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-1 focus-within:ring-ring"
        onClick={() => setDropdownOpen(true)}
      >
        <div className="flex flex-wrap gap-1 pr-6">
          {selectedTopics.map((topic) => (
            <Badge
              key={topic}
              className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground hover:bg-primary/80"
            >
              <span className="max-w-[140px] truncate">{topic}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTopic(topic);
                }}
                className="ml-1 rounded-full outline-none"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <input
            placeholder={selectedTopics.length === 0 ? placeholder : ""}
            className="min-w-[80px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setDropdownOpen(true)}
          />
        </div>

        {selectedTopics.length > 0 && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
          >
            <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {dropdownOpen && availableTopics.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
          {/* Select All */}
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent",
              allSelected && "bg-accent"
            )}
            onClick={allSelected ? clearAll : selectAll}
          >
            <div
              className={cn(
                "flex h-4 w-4 items-center justify-center rounded-sm border",
                allSelected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-muted-foreground/30"
              )}
            >
              {allSelected && <Check className="h-3 w-3" />}
            </div>
            <span className="font-medium text-primary">Select All</span>
          </button>

          {filteredTopics.map((topic) => {
            const isSelected = selectedTopics.includes(topic);
            return (
              <button
                key={topic}
                type="button"
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent",
                  isSelected && "bg-accent"
                )}
                onClick={() => toggleTopic(topic)}
              >
                <div
                  className={cn(
                    "flex h-4 w-4 items-center justify-center rounded-sm border",
                    isSelected
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-muted-foreground/30"
                  )}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                </div>
                <span className="truncate">{topic}</span>
              </button>
            );
          })}

          {filteredTopics.length === 0 && (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No concepts found.
            </div>
          )}
        </div>
      )}

      {dropdownOpen && availableTopics.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-3 shadow-md">
          <p className="text-sm text-muted-foreground">No concepts available.</p>
        </div>
      )}
    </div>
  );
};
